// @flow

import type {IntlShape} from 'react-intl';
import type Moment from 'moment';

import moment from 'moment';

type MaybeDate = string | Date | null;

export type MaybeMoment = Moment | null;

export const clearDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const toMoment = (date: MaybeDate): MaybeMoment => (date === null ? null : moment(date));

export const formatDate = (date: MaybeDate): Date | null => {
  const maybeMoment = toMoment(date);
  return maybeMoment === null ? null : maybeMoment.toDate();
};

export const formatDateRange = ({from, to}: {|from: MaybeDate, to: MaybeDate|}) => ({
  from: formatDate(from),
  to: formatDate(to)
});

export const getToday = () => moment().startOf('day');

export const disableDatesBefore = (date: Moment) => (current: MaybeDate) =>
  current ? moment(current).isBefore(date) : true;

export const getIntlFormatDateAndTime = (
  date: number,
  intl: IntlShape,
  delimiter: string = ', '
): string =>
  [
    intl.formatDate(date, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    }),
    intl.formatTime(date)
  ].join(delimiter);

export const getServerValidDateString = (date: Date): MaybeDate => {
  if (date) {
    return moment(new Date(date)).format('YYYY-MM-DDTHH:mm:ss');
  }

  return null;
};

export const getCurrentTimezone = (): string => {
  const raw = (new Date().getTimezoneOffset() / 60) * -1;
  const formatted = raw
    .toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      minimumFractionDigits: 2
    })
    .replace('.', ':');

  if (raw > 0) {
    return `+${formatted}`;
  }

  return formatted;
};

export const getBrowserTimezone = (): string => {
  const offsetNumber = moment().utcOffset() / 60;

  if (offsetNumber > 0) {
    return `+${offsetNumber}`;
  }

  return `${offsetNumber}`;
};

/* Format Utc Offset from +05:00 to +5 */
export const formatUtcOffset = (utcOffset: string): string => {
  const FORMAT_UTC_REGEXP = /^(\+|\-)([1-9]|[0-0])(\d)(\:[0-9][0-9])$/gi;
  return utcOffset.replace(FORMAT_UTC_REGEXP, (match, p1, p2, p3) => {
    if (parseInt(p2) === 0) {
      return `${p1}${p3}`;
    } else {
      return `${p1}${p2}${p3}`;
    }
  });
};

export const getTimezoneFromDate = (date: string | null): string =>
  date ? date.substr(-6) : moment().format('Z');

export const ignoreTimezone = (date: string | null): string | null =>
  date ? date.substr(0, 19) : date;

/* Format Utc one number (for example +5) */
export const getTimeZoneOneNumber = (): string | number => {
  const zoneOffsetHours = new Date().getTimezoneOffset() / -60;

  return zoneOffsetHours > 0 ? `+${zoneOffsetHours}` : zoneOffsetHours;
};
